import { useCallback, useEffect, useState } from "react";
import { LogOut, RefreshCw } from "lucide-react";
import axios from "axios";
import { Overline } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "je_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [enquiries, setEnquiries] = useState([]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const load = useCallback(
    async (t = token) => {
      setStatus("loading");
      try {
        const res = await axios.get(`${API}/admin/enquiries`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        setEnquiries(res.data);
        setStatus("idle");
      } catch (err) {
        setStatus("idle");
        if (err.response?.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken("");
        }
      }
    },
    [token]
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setStatus("idle");
    } catch (err) {
      setStatus("idle");
      setError("Invalid password.");
    }
  };

  const markContacted = async (id) => {
    try {
      await axios.patch(
        `${API}/admin/enquiries/${id}`,
        { status: "contacted" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEnquiries((list) =>
        list.map((enq) => (enq.id === id ? { ...enq, status: "contacted" } : enq))
      );
    } catch (err) {
      /* noop */
    }
  };

  if (!token) {
    return (
      <main data-testid="admin-login-page" className="min-h-screen flex items-center justify-center px-5 pt-24 pb-16">
        <form onSubmit={login} className="w-full max-w-sm border border-ink/15 bg-white p-8 sm:p-10">
          <Overline className="mb-3">Jagdamba Enterprises</Overline>
          <h1 className="font-display font-black uppercase tracking-tight text-3xl mb-8">
            Admin Login
          </h1>
          <label htmlFor="admin-password" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 mb-2">
            Password
          </label>
          <input
            id="admin-password"
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent border border-ink/25 px-4 py-3 text-sm focus:outline-none focus:border-ink transition-colors"
            placeholder="Admin password"
          />
          {error && (
            <p data-testid="admin-login-error" className="mt-4 text-sm text-red-700">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={status === "loading"}
            className="mt-6 w-full bg-ink text-paper font-mono text-xs tracking-[0.2em] uppercase px-6 py-4 hover:bg-accent hover:text-ink transition-colors disabled:opacity-60"
          >
            {status === "loading" ? "Checking…" : "Login"}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="min-h-screen px-5 sm:px-8 lg:px-12 pt-32 sm:pt-40 pb-20">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div>
          <Overline className="mb-2">Jagdamba Enterprises</Overline>
          <h1 className="font-display font-black uppercase tracking-tight text-3xl sm:text-5xl">
            Enquiries
          </h1>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="admin-refresh-button"
            onClick={() => load()}
            className="inline-flex items-center gap-2 border border-ink/30 font-mono text-[11px] tracking-[0.2em] uppercase px-5 py-3 hover:border-ink transition-colors"
          >
            <RefreshCw size={13} /> Refresh
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={() => {
              sessionStorage.removeItem(TOKEN_KEY);
              setToken("");
            }}
            className="inline-flex items-center gap-2 bg-ink text-paper font-mono text-[11px] tracking-[0.2em] uppercase px-5 py-3 hover:bg-accent hover:text-ink transition-colors"
          >
            <LogOut size={13} /> Logout
          </button>
        </div>
      </div>

      {enquiries.length === 0 ? (
        <p data-testid="admin-empty-state" className="py-20 text-ink/50 font-mono text-xs tracking-[0.2em] uppercase">
          {status === "loading" ? "Loading…" : "No enquiries yet."}
        </p>
      ) : (
        <div className="overflow-x-auto border border-ink/15" data-testid="admin-enquiries-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="bg-ink text-paper text-left">
                {["Date", "Name", "Company", "Phone", "Email", "Product", "Qty", "Requirement", "Status", ""].map(
                  (h, i) => (
                    <th key={i} className="px-4 py-3 font-mono text-[10px] tracking-[0.2em] uppercase font-medium">
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {enquiries.map((enq) => (
                <tr key={enq.id} className="border-t border-ink/10 align-top hover:bg-white transition-colors" data-testid={`admin-enquiry-row-${enq.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap text-ink/60">
                    {new Date(enq.created_at).toLocaleString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="px-4 py-3 font-medium">{enq.name}</td>
                  <td className="px-4 py-3 text-ink/60">{enq.company || "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <a href={`tel:${enq.phone.replace(/\s/g, "")}`} className="link-underline">
                      {enq.phone}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-ink/60">{enq.email || "—"}</td>
                  <td className="px-4 py-3">{enq.product || "—"}</td>
                  <td className="px-4 py-3 text-ink/60">{enq.quantity || "—"}</td>
                  <td className="px-4 py-3 max-w-[260px]">
                    <span className="block truncate" title={enq.requirement}>
                      {enq.requirement}
                    </span>
                    {enq.message && (
                      <span className="block truncate text-ink/50 text-xs" title={enq.message}>
                        {enq.message}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`font-mono text-[10px] tracking-[0.15em] uppercase px-2 py-1 ${
                        enq.status === "new" ? "bg-accent text-ink" : "bg-ink/10 text-ink/60"
                      }`}
                    >
                      {enq.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {enq.status === "new" && (
                      <button
                        data-testid={`admin-mark-contacted-${enq.id}`}
                        onClick={() => markContacted(enq.id)}
                        className="font-mono text-[10px] tracking-[0.15em] uppercase link-underline whitespace-nowrap"
                      >
                        Mark Contacted
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
